import axios from 'axios'

function getV2AdminBase() {
    const v1Base = axios.defaults.baseURL || ''
    return v1Base.replace(/\/api\/v1\/?$/, '/api/v2/admin/')
}

function v2(path) {
    return getV2AdminBase() + path
}

export default {
    // ==================== IoT Device Management ====================

    /**
     * Fetch all IoT devices with optional filtering
     * @param {Object} params - Query parameters (status, device_type, gateway_id, page, per_page)
     */
    async fetchDevices(params = {}) {
        const queryString = new URLSearchParams(params).toString()
        return await axios.get(`iot-devices${queryString ? '?' + queryString : ''}`)
    },

    /**
     * Fetch pending (unassigned) devices
     */
    async fetchPendingDevices() {
        return await axios.get('iot-devices/pending')
    },

    /**
     * Fetch device by ID
     * @param {Number} id - Device ID
     */
    async fetchDeviceById(id) {
        return await axios.get(`iot-devices/${id}`)
    },

    /**
     * Assign device to an asset
     * @param {Number} deviceId - Device ID
     * @param {Number} assetId - Asset ID to assign
     * @param {Object} customThresholds - Optional custom thresholds
     * @param {Number} postingInterval - Optional posting interval override
     */
    async assignDevice(deviceId, assetId, customThresholds = null, postingInterval = null) {
        return await axios.post(`iot-devices/${deviceId}/assign`, {
            asset_id: assetId,
            custom_thresholds: customThresholds,
            posting_interval: postingInterval
        })
    },

    /**
     * Activate device (start collecting telemetry)
     * @param {Number} deviceId - Device ID
     */
    async activateDevice(deviceId) {
        return await axios.post(`iot-devices/${deviceId}/activate`)
    },

    /**
     * Deactivate device
     * @param {Number} deviceId - Device ID
     */
    async deactivateDevice(deviceId) {
        return await axios.post(`iot-devices/${deviceId}/deactivate`)
    },

    /**
     * Update device settings
     * @param {Number} deviceId - Device ID
     * @param {Object} data - Settings to update
     */
    async updateDevice(deviceId, data) {
        return await axios.put(`iot-devices/${deviceId}`, data)
    },

    /**
     * Delete device
     * @param {Number} deviceId - Device ID
     */
    async deleteDevice(deviceId) {
        return await axios.delete(`iot-devices/${deviceId}`)
    },

    // ==================== IoT Device Types ====================

    /**
     * Fetch all device types
     */
    async fetchDeviceTypes() {
        return await axios.get('iot-devices/types')
    },

    /**
     * Fetch device type by ID
     * @param {Number} id - Device type ID
     */
    async fetchDeviceTypeById(id) {
        return await axios.get(`iot-devices/types/${id}`)
    },

    /**
     * Create new device type
     * @param {Object} data - Device type data (name, capabilities, default_thresholds, etc.)
     */
    async createDeviceType(data) {
        return await axios.post('iot-devices/types', data)
    },

    /**
     * Update device type
     * @param {Number} id - Device type ID
     * @param {Object} data - Updated device type data
     */
    async updateDeviceType(id, data) {
        return await axios.put(`iot-devices/types/${id}`, data)
    },

    /**
     * Delete device type
     * @param {Number} id - Device type ID
     */
    async deleteDeviceType(id) {
        return await axios.delete(`iot-devices/types/${id}`)
    },

    // ==================== Gateway Management ====================

    /**
     * Fetch all gateways
     */
    async fetchGateways() {
        return await axios.get('iot-devices/gateways')
    },

    /**
     * Fetch gateway by ID
     * @param {Number} id - Gateway ID
     */
    async fetchGatewayById(id) {
        return await axios.get(`iot-devices/gateways/${id}`)
    },

    /**
     * Create new gateway
     * @param {Object} data - Gateway data (gateway_id, name, ip_address, configuration, etc.)
     */
    async createGateway(data) {
        return await axios.post('iot-devices/gateways', data)
    },

    /**
     * Update gateway
     * @param {Number} id - Gateway ID
     * @param {Object} data - Updated gateway data
     */
    async updateGateway(id, data) {
        return await axios.put(`iot-devices/gateways/${id}`, data)
    },

    /**
     * Delete gateway
     * @param {Number} id - Gateway ID
     */
    async deleteGateway(id) {
        return await axios.delete(`iot-devices/gateways/${id}`)
    },

    /**
     * Record gateway heartbeat
     * @param {Number} id - Gateway ID
     * @param {Object} data - Heartbeat data (firmware_version, connected_devices, etc.)
     */
    async gatewayHeartbeat(id, data = {}) {
        return await axios.post(`iot-devices/gateways/${id}/heartbeat`, data)
    },

    // ==================== Telemetry Data ====================

    /**
     * Fetch telemetry data for an asset
     * @param {Number} assetId - Asset ID
     * @param {Object} params - Query parameters (start_date, end_date, device_id, limit)
     */
    async fetchAssetTelemetry(assetId, params = {}) {
        const queryString = new URLSearchParams(params).toString()
        return await axios.get(`iot-devices/assets/${assetId}/telemetry${queryString ? '?' + queryString : ''}`)
    },

    /**
     * Fetch latest telemetry reading for an asset
     * @param {Number} assetId - Asset ID
     */
    async fetchLatestTelemetry(assetId) {
        return await axios.get(`iot-devices/assets/${assetId}/telemetry/latest`)
    },

    /**
     * Fetch telemetry statistics for an asset
     * @param {Number} assetId - Asset ID
     * @param {Object} params - Query parameters (start_date, end_date, aggregation)
     */
    async fetchTelemetryStats(assetId, params = {}) {
        const queryString = new URLSearchParams(params).toString()
        return await axios.get(`iot-devices/assets/${assetId}/telemetry/stats${queryString ? '?' + queryString : ''}`)
    },

    /**
     * Fetch telemetry alerts
     * @param {Object} params - Query parameters (asset_id, start_date, end_date, severity)
     */
    async fetchTelemetryAlerts(params = {}) {
        const queryString = new URLSearchParams(params).toString()
        return await axios.get(`iot-devices/telemetry/alerts${queryString ? '?' + queryString : ''}`)
    },

    /**
     * Post telemetry data (for testing)
     * @param {Array} readings - Array of telemetry readings
     */
    async postTelemetry(readings) {
        return await axios.post('iot-devices/telemetry', {
            readings
        })
    },

    // ==================== V2 Dashboard ====================

    async fetchDashboardSummary() {
        return await axios.get(v2('iot/dashboard/summary'))
    },

    async fetchDeviceHealth() {
        return await axios.get(v2('iot/dashboard/device-health'))
    },

    async fetchBatteryLevels() {
        return await axios.get(v2('iot/dashboard/battery-levels'))
    },

    async fetchOfflineDevices() {
        return await axios.get(v2('iot/dashboard/offline-devices'))
    },

    async fetchTelemetryTrends(params = {}) {
        const queryString = new URLSearchParams(params).toString()
        return await axios.get(`${v2('iot/dashboard/telemetry-trends')}${queryString ? '?' + queryString : ''}`)
    },

    // ==================== V2 Alerts ====================

    async fetchAlerts(params = {}) {
        const queryString = new URLSearchParams(params).toString()
        return await axios.get(`${v2('iot/alerts')}${queryString ? '?' + queryString : ''}`)
    },

    async fetchAlertById(id) {
        return await axios.get(v2(`iot/alerts/${id}`))
    },

    async fetchAlertStatistics() {
        return await axios.get(v2('iot/alerts/statistics'))
    },

    async acknowledgeAlert(id, data = {}) {
        return await axios.post(v2(`iot/alerts/${id}/acknowledge`), data)
    },

    async resolveAlert(id, data = {}) {
        return await axios.post(v2(`iot/alerts/${id}/resolve`), data)
    },

    async bulkAcknowledgeAlerts(ids) {
        return await axios.post(v2('iot/alerts/bulk-acknowledge'), { ids })
    },

    async bulkResolveAlerts(ids) {
        return await axios.post(v2('iot/alerts/bulk-resolve'), { ids })
    },

    // ==================== V2 Readings ====================

    async fetchReadings(params = {}) {
        const queryString = new URLSearchParams(params).toString()
        return await axios.get(`${v2('iot/readings')}${queryString ? '?' + queryString : ''}`)
    },

    async fetchReadingsByAsset(assetId, params = {}) {
        const queryString = new URLSearchParams(params).toString()
        return await axios.get(`${v2(`iot/readings/asset/${assetId}`)}${queryString ? '?' + queryString : ''}`)
    },

    async fetchScheduledReadings(params = {}) {
        const queryString = new URLSearchParams(params).toString()
        return await axios.get(`${v2('iot/readings/scheduled')}${queryString ? '?' + queryString : ''}`)
    },

    async fetchReadingGaps(params = {}) {
        const queryString = new URLSearchParams(params).toString()
        return await axios.get(`${v2('iot/readings/gaps')}${queryString ? '?' + queryString : ''}`)
    },

    async fetchRawTelemetry(assetId, params = {}) {
        const queryString = new URLSearchParams(params).toString()
        return await axios.get(`${v2(`iot/readings/raw/${assetId}`)}${queryString ? '?' + queryString : ''}`)
    },

    async submitManualReading(data) {
        return await axios.post(v2('iot/readings/manual'), data)
    },

    async exportReadingsCSV(params = {}) {
        const queryString = new URLSearchParams(params).toString()
        return await axios.get(`${v2('iot/readings/export/csv')}${queryString ? '?' + queryString : ''}`, {
            responseType: 'blob'
        })
    },

    async exportReadingsPDF(params = {}) {
        const queryString = new URLSearchParams(params).toString()
        return await axios.get(`${v2('iot/readings/export/pdf')}${queryString ? '?' + queryString : ''}`, {
            responseType: 'blob'
        })
    },

    // ==================== Export & Reports ====================

    async exportTelemetry(assetId, params = {}) {
        const queryString = new URLSearchParams(params).toString()
        return await axios.get(`iot-devices/assets/${assetId}/telemetry/export${queryString ? '?' + queryString : ''}`, {
            responseType: 'blob'
        })
    },

    async exportDevices(params = {}) {
        const queryString = new URLSearchParams(params).toString()
        return await axios.get(`iot-devices/export${queryString ? '?' + queryString : ''}`, {
            responseType: 'blob'
        })
    }
}